import { useState } from "react";
import { DollarSign, CalendarDays, Split, CreditCard, Banknote, QrCode, Smartphone, Truck, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection, { inputClass } from "./ConfigSection";

const CARD_BRANDS = [
  { key: "visa", label: "Visa" },
  { key: "mastercard", label: "Mastercard" },
  { key: "elo", label: "Elo" },
  { key: "amex", label: "American Express" },
  { key: "hipercard", label: "Hipercard" },
  { key: "dinners", label: "Diners Club" },
];

const ONLINE_METHODS = [
  { key: "credit_card", label: "Cartão de Crédito", icon: CreditCard },
  { key: "debit_card", label: "Cartão de Débito", icon: CreditCard },
  { key: "pix", label: "PIX", icon: QrCode },
];

interface PaymentMethodsConfig {
  delivery: {
    pix: boolean;
    cash: boolean;
    card: Record<string, boolean>;
  };
  online: Record<string, boolean>;
  local: {
    pix: boolean;
    cash: boolean;
    card: Record<string, boolean>;
  };
}

const defaultPaymentMethods: PaymentMethodsConfig = {
  delivery: { pix: true, cash: true, card: { visa: true, mastercard: true, elo: true, amex: true, hipercard: true, dinners: true } },
  online: { credit_card: true, debit_card: true, pix: true },
  local: { pix: true, cash: true, card: { visa: true, mastercard: true, elo: true, amex: true, hipercard: true, dinners: true } },
};

const PaymentMethodCardBrands = ({
  methods,
  section,
  onToggle,
}: {
  methods: PaymentMethodsConfig;
  section: "delivery" | "local";
  onToggle: (section: "delivery" | "local", brand: string) => void;
}) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 ml-8 mt-2">
    {CARD_BRANDS.map((brand) => (
      <label key={brand.key} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border border-border hover:bg-secondary/50 transition-colors">
        <Checkbox
          checked={!!methods[section].card[brand.key]}
          onCheckedChange={() => onToggle(section, brand.key)}
        />
        <span className="text-xs font-medium">{brand.label}</span>
      </label>
    ))}
  </div>
);

const Pagamento = () => {
  const { form, update } = useConfig();
  const [showMpToken, setShowMpToken] = useState(false);

  const methods: PaymentMethodsConfig = {
    ...defaultPaymentMethods,
    ...(form as any).accepted_payment_methods,
    delivery: {
      ...defaultPaymentMethods.delivery,
      ...((form as any).accepted_payment_methods?.delivery || {}),
      card: {
        ...defaultPaymentMethods.delivery.card,
        ...((form as any).accepted_payment_methods?.delivery?.card || {}),
      },
    },
    online: {
      ...defaultPaymentMethods.online,
      ...((form as any).accepted_payment_methods?.online || {}),
    },
    local: {
      ...defaultPaymentMethods.local,
      ...((form as any).accepted_payment_methods?.local || {}),
      card: {
        ...defaultPaymentMethods.local.card,
        ...((form as any).accepted_payment_methods?.local?.card || {}),
      },
    },
  };

  const updateMethods = (updated: PaymentMethodsConfig) => {
    update("accepted_payment_methods", updated);
  };

  const toggleDeliveryBase = (key: "pix" | "cash") => {
    const updated = { ...methods, delivery: { ...methods.delivery, [key]: !methods.delivery[key] } };
    updateMethods(updated);
  };

  const toggleDeliveryCard = (brand: string) => {
    const updated = {
      ...methods,
      delivery: { ...methods.delivery, card: { ...methods.delivery.card, [brand]: !methods.delivery.card[brand] } },
    };
    updateMethods(updated);
  };

  const toggleLocalBase = (key: "pix" | "cash") => {
    const updated = { ...methods, local: { ...methods.local, [key]: !methods.local[key] } };
    updateMethods(updated);
  };

  const toggleLocalCard = (brand: string) => {
    const updated = {
      ...methods,
      local: { ...methods.local, card: { ...methods.local.card, [brand]: !methods.local.card[brand] } },
    };
    updateMethods(updated);
  };

  const toggleOnline = (key: string) => {
    const updated = { ...methods, online: { ...methods.online, [key]: !methods.online[key] } };
    updateMethods(updated);
  };

  const hasAnyDeliveryCard = Object.values(methods.delivery.card).some(Boolean);
  const hasAnyLocalCard = Object.values(methods.local.card).some(Boolean);

  return (
    <div className="space-y-6">
      {/* Delivery payment methods */}
      <ConfigSection title="Pagamento na Entrega" icon={Banknote} description="Formas de pagamento aceitas para pedidos de delivery">
        <div className="space-y-3">
          {/* PIX */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <QrCode className="h-4.5 w-4.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium">PIX</p>
                <p className="text-[11px] text-muted-foreground">Pagamento instantâneo via PIX</p>
              </div>
            </div>
            <Switch checked={methods.delivery.pix} onCheckedChange={() => toggleDeliveryBase("pix")} />
          </div>

          {/* Cash */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Banknote className="h-4.5 w-4.5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Dinheiro</p>
                <p className="text-[11px] text-muted-foreground">Pagamento em espécie com troco</p>
              </div>
            </div>
            <Switch checked={methods.delivery.cash} onCheckedChange={() => toggleDeliveryBase("cash")} />
          </div>

          {/* Card */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <CreditCard className="h-4.5 w-4.5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Cartão</p>
                  <p className="text-[11px] text-muted-foreground">Selecione as bandeiras aceitas</p>
                </div>
              </div>
              <span className="text-[11px] text-muted-foreground">{Object.values(methods.delivery.card).filter(Boolean).length} bandeiras</span>
            </div>
            <div className="px-3 pb-3">
              <PaymentMethodCardBrands methods={methods} section="delivery" onToggle={(_, brand) => toggleDeliveryCard(brand)} />
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* Online payment methods (MercadoPago) */}
      <ConfigSection title="Pagamento Online (MercadoPago)" icon={Smartphone} description="Formas de pagamento processadas online via MercadoPago">
        <div className="p-3 rounded-lg bg-secondary/50 text-sm text-muted-foreground mb-2">
          Configure suas credenciais do MercadoPago para receber pagamentos diretamente na sua conta.
          Obtenha suas credenciais em{" "}
          <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noopener noreferrer" className="text-primary underline">
            mercadopago.com.br/developers
          </a>{" "}
          → Suas integrações → Credenciais de produção.
        </div>

        {!form.mp_access_token && (
          <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-300 text-xs">
              Sem credenciais configuradas. Os pagamentos online (PIX e Cartão) estão desativados no checkout. Configure seu Access Token para ativar.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {ONLINE_METHODS.map((method) => (
            <div key={method.key} className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <method.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <p className="text-sm font-medium">{method.label}</p>
              </div>
              <Switch checked={!!methods.online[method.key]} onCheckedChange={() => toggleOnline(method.key)} />
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-3 pt-4 border-t border-border">
          <div className="space-y-2">
            <Label>Access Token</Label>
            <div className="flex gap-2">
              <Input
                type={showMpToken ? "text" : "password"}
                value={form.mp_access_token}
                onChange={(e) => update("mp_access_token", e.target.value)}
                className={inputClass}
                placeholder="APP_USR-..."
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 px-3 shrink-0"
                onClick={() => setShowMpToken(!showMpToken)}
              >
                {showMpToken ? "Ocultar" : "Mostrar"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Token de produção usado para processar pagamentos reais.</p>
          </div>
          <div className="space-y-2">
            <Label>Public Key (opcional)</Label>
            <Input
              value={form.mp_public_key}
              onChange={(e) => update("mp_public_key", e.target.value)}
              className={inputClass}
              placeholder="APP_USR-..."
            />
            <p className="text-[11px] text-muted-foreground">Chave pública para checkout transparente (opcional).</p>
          </div>
        </div>
      </ConfigSection>

      {/* Local payment methods */}
      <ConfigSection title="Pagamento no Local" icon={DollarSign} description="Formas de pagamento para mesa, balcão e PDV">
        <div className="space-y-3">
          {/* PIX */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <QrCode className="h-4.5 w-4.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium">PIX</p>
                <p className="text-[11px] text-muted-foreground">Pagamento instantâneo via PIX</p>
              </div>
            </div>
            <Switch checked={methods.local.pix} onCheckedChange={() => toggleLocalBase("pix")} />
          </div>

          {/* Cash */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Banknote className="h-4.5 w-4.5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Dinheiro</p>
                <p className="text-[11px] text-muted-foreground">Pagamento em espécie</p>
              </div>
            </div>
            <Switch checked={methods.local.cash} onCheckedChange={() => toggleLocalBase("cash")} />
          </div>

          {/* Card */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <CreditCard className="h-4.5 w-4.5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Cartão</p>
                  <p className="text-[11px] text-muted-foreground">Selecione as bandeiras aceitas</p>
                </div>
              </div>
              <span className="text-[11px] text-muted-foreground">{Object.values(methods.local.card).filter(Boolean).length} bandeiras</span>
            </div>
            <div className="px-3 pb-3">
              <PaymentMethodCardBrands methods={methods} section="local" onToggle={(_, brand) => toggleLocalCard(brand)} />
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* Delivery Tip Config */}
      <ConfigSection title="Gorjeta para Motoboy" icon={Truck} description="Permite que clientes enviem gorjetas via PIX para o entregador">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Truck className="h-4.5 w-4.5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Ativar gorjeta para motoboy</p>
                <p className="text-[11px] text-muted-foreground">Exibe opção de gorjeta via PIX na tela de acompanhamento</p>
              </div>
            </div>
            <Switch
              checked={!!(form as any).tip_enabled}
              onCheckedChange={(v) => update("tip_enabled", v)}
            />
          </div>

          {(form as any).tip_enabled && (
            <div className="space-y-2">
              <Label>Valores sugeridos (separados por vírgula)</Label>
              <Input
                value={(() => {
                  const vals = (form as any).tip_values;
                  if (Array.isArray(vals)) return vals.join(", ");
                  return "2, 5, 10";
                })()}
                onChange={(e) => {
                  const vals = e.target.value.split(",").map((v: string) => Number(v.trim())).filter((v: number) => v > 0);
                  update("tip_values", vals.length > 0 ? vals : [2, 5, 10]);
                }}
                className={inputClass}
                placeholder="2, 5, 10"
              />
              <p className="text-[11px] text-muted-foreground">O pagamento é processado via Mercado Pago PIX.</p>
            </div>
          )}
        </div>
      </ConfigSection>

      {/* Order features */}
      <ConfigSection title="Funcionalidades de Pedido" icon={CalendarDays}>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <CalendarDays className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Agendamento de Pedidos</p>
                <p className="text-[11px] text-muted-foreground">Clientes podem agendar pedidos para data e horário futuro</p>
              </div>
            </div>
            <Switch
              checked={form.scheduled_orders_enabled}
              onCheckedChange={(v) => update("scheduled_orders_enabled", v)}
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Split className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Dividir Conta (Split)</p>
                <p className="text-[11px] text-muted-foreground">Clientes podem calcular divisão de conta no checkout</p>
              </div>
            </div>
            <Switch
              checked={form.split_payment_enabled}
              onCheckedChange={(v) => update("split_payment_enabled", v)}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );
};

export default Pagamento;
